import { Head, usePage } from '@inertiajs/react'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import MainLayout from '@/components/Layout/MainLayout'
import { Button } from '@/components/ui/Button'
import { HelpCenterHeader } from '@/components/help_center/HelpCenterHeader'
import { HelpBannerList } from '@/components/help_center/HelpBannerList'
import { BannerFormDialog } from '@/components/help_center/BannerFormDialog'
import type { CurrentUser } from '@/types/shared'
import type { HelpBannerRow } from './types'

interface BannersProps {
  current_user: CurrentUser
  banners: HelpBannerRow[]
  categories: { id: string; name: string }[]
  available_tags: string[]
  brand_primary: string | null
}

export default function Banners({ current_user, banners, categories, available_tags, brand_primary }: BannersProps) {
  const { flash } = usePage<{ flash: { notice?: string; alert?: string } }>().props
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<HelpBannerRow | null>(null)

  useEffect(() => {
    if (flash?.notice) toast.success(flash.notice)
    if (flash?.alert) toast.error(flash.alert)
  }, [flash?.notice, flash?.alert])

  const openNew = () => { setEditing(null); setDialogOpen(true) }
  const openEdit = (banner: HelpBannerRow) => { setEditing(banner); setDialogOpen(true) }

  return (
    <>
      <Head title="Help center banners" />
      <MainLayout user={current_user} breadcrumbs={[{ label: 'Help center' }, { label: 'Banners' }]}>
        <div className="flex-1 overflow-y-auto p-6">
          <HelpCenterHeader
            actions={<Button onClick={openNew}><Plus className="size-4" /> New banner</Button>}
          />

          <p className="mb-4 max-w-2xl text-sm text-muted-foreground">
            Banners sit above the content on your public help center. Point one at a tag or a category to
            flag a group of articles at once, for example marking the docs for an older version.
          </p>

          <HelpBannerList
            banners={banners}
            categories={categories}
            brandPrimary={brand_primary}
            onEdit={openEdit}
          />
        </div>
      </MainLayout>

      {/* The key gives each banner a clean Tiptap instance; the dialog also
          resets its own form whenever it opens, which covers reopening the
          same key (new, new) or the same banner after a save. */}
      <BannerFormDialog
        key={editing?.id ?? 'new'}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        banner={editing}
        categories={categories}
        availableTags={available_tags}
        brandPrimary={brand_primary}
      />
    </>
  )
}
